import { z } from 'zod'

// Schema for question options
const optionSchema = z.object({
  id: z.string(),
  text: z.string(),
  isCorrect: z.boolean(),
  order: z.number(),
  questionId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
})

// Schema for quiz questions
const questionSchema = z.object({
  id: z.string(),
  question: z.string(),
  type: z.enum(['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'ESSAY']),
  points: z.number(),
  order: z.number(),
  isRequired: z.boolean(),
  quizId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  options: z.array(optionSchema),
})

// Schema for module info (nested in quiz)
const moduleInfoSchema = z.object({
  id: z.string(),
  title: z.string(),
  course: z.object({
    id: z.string(),
    title: z.string(),
    slug: z.string(),
  }),
})

// Schema for creator info (nested in quiz)
const creatorSchema = z.object({
  id: z.string(),
  name: z.string(),
  avatar: z.string().nullable(),
})

// Schema for quiz count
const quizCountSchema = z.object({
  questions: z.number(),
  attempts: z.number(),
})

// Main quiz schema
export const quizSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  instructions: z.string(),
  timeLimit: z.number(), // Time limit in minutes
  passingScore: z.number(), // Passing score percentage
  maxAttempts: z.number(),
  isRandomized: z.boolean(),
  isPublished: z.boolean(),
  moduleId: z.string(),
  creatorId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  module: moduleInfoSchema,
  creator: creatorSchema,
  questions: z.array(questionSchema),
  _count: quizCountSchema,
})

export type Quiz = z.infer<typeof quizSchema>
export type Question = z.infer<typeof questionSchema>
export type Option = z.infer<typeof optionSchema>

// Schema for the quizzes API response (after apiClient extracts data.data)
export const quizzesApiResponseSchema = z.object({
  quizzes: z.array(quizSchema),
  total: z.number(),
  page: z.number(),
  totalPages: z.number(),
})

export type QuizzesApiResponse = z.infer<typeof quizzesApiResponseSchema>

// Schema for the complete API response (before apiClient processing)
export const fullQuizzesApiResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    quizzes: z.array(quizSchema),
    total: z.number(),
    page: z.number(),
    totalPages: z.number(),
  }),
  messages: z.array(z.string()).optional(),
  errors: z.array(z.string()).optional(),
})

// Schema for array of quizzes (for backward compatibility)
export const quizzesArraySchema = z.array(quizSchema)
